import type {
	WorkfrontAssetTypeMappingRowInput,
	WorkfrontFieldMappingRowInput,
	WorkfrontLookupRowInput,
	WorkfrontValueMappingRowInput,
} from '@atlas/models'
import type {
	ICanonicalBundle,
	IWorkfrontDraftChange,
	IWorkfrontMappingPreview,
	IWorkfrontMappingSummaryCounts,
	IWorkfrontReferenceEntry,
	IWorkfrontTarget,
	IWorkfrontValueProposal,
	IWorkfrontValueRow,
	PaginatedResponse,
	WorkfrontLookup,
	WorkfrontMappingKind,
	WorkfrontValueMatch,
} from '@atlas/types'
import { apiClient } from './api.client'

/** Where a row stands against the open draft, as the API marks it. */
export type MappingRowStatus = 'published' | 'added' | 'changed' | 'deleted'

export type FieldRow = WorkfrontFieldMappingRowInput
export type AssetTypeRow = WorkfrontAssetTypeMappingRowInput
export type LookupRow = WorkfrontLookupRowInput
export type ValueRow = WorkfrontValueMappingRowInput

/** The row of any kind, as a draft change carries it. */
export type MappingRow = FieldRow | AssetTypeRow | LookupRow | ValueRow

export interface MappingVersionDTO {
	version: number
	publishedBy: string
	publishedAt: string
	summary: string
	counts: IWorkfrontMappingSummaryCounts
	republishedFrom: number | null
}

export interface MappingDraftDTO {
	id: string
	revision: number
	openedBy: string
	openedAt: string
	changes: IWorkfrontDraftChange[]
	counts: IWorkfrontMappingSummaryCounts
}

export interface MappingStatusDTO {
	version: number
	latest: MappingVersionDTO | null
	draft: MappingDraftDTO | null
}

interface Annotated<T> {
	key: string
	status: MappingRowStatus
	row: T
}

export interface FieldMappingListRow extends Annotated<FieldRow> {
	sourceLabel: string | null
	fallbackLabel: string | null
	target: IWorkfrontTarget | null
}

export type AssetTypeListRow = Annotated<AssetTypeRow>

export interface LookupListRow extends Annotated<LookupRow> {
	sourceLabel: string | null
}

/** A Workfront label of an axis; `unmapped` while no value mapping covers it. */
export interface ValueListRow extends IWorkfrontValueRow {
	key: string
	status: MappingRowStatus | 'unmapped'
}

export interface UnmappedFieldDTO {
	parameterId: string
	label: string | null
	name: string | null
	objCodes: string[]
	forms: string[]
	objectCount: number
	sampleValues: string[]
	firstSeenAt: string | null
	lastSeenAt: string | null
}

export type MappingPreviewDTO = IWorkfrontMappingPreview

/** A Workfront parameter of the reference copy, as the field picker lists it. */
export interface WorkfrontParameterDTO extends Omit<IWorkfrontReferenceEntry, 'refreshedAt'> {
	refreshedAt: string
}

export interface BundleCanonicalDTO {
	bundleId: string
	version: number
	mappingVersion: number
	canonical: ICanonicalBundle
	canonicalHash: string
	held: ICanonicalBundle['held']
	warnings: ICanonicalBundle['warnings']
	mappedAt: string
}

export interface ValueListParams {
	axis?: string
	match?: WorkfrontValueMatch
	waiting?: boolean
}

const BASE = '/admin/workfront/mappings'
const data = <T>(r: { data: { data: T } }): T => r.data.data

/** Keys hold `|`, so they always travel encoded. */
const keyPath = (kind: WorkfrontMappingKind, key: string) => `${BASE}/draft/${kind}/${encodeURIComponent(key)}`

/** The Mappings admin API: the tabs' reads, the draft, and the publishing commands. */
export const workfrontMappingsApi = {
	catalogue: (): Promise<IWorkfrontTarget[]> => apiClient.get(`${BASE}/catalogue`).then(data<IWorkfrontTarget[]>),
	status: (): Promise<MappingStatusDTO> => apiClient.get<MappingStatusDTO>(`${BASE}/status`).then((r) => r.data),
	fields: (): Promise<FieldMappingListRow[]> => apiClient.get(`${BASE}/fields`).then(data<FieldMappingListRow[]>),
	assetTypes: (): Promise<AssetTypeListRow[]> => apiClient.get(`${BASE}/asset-types`).then(data<AssetTypeListRow[]>),
	lookups: (lookup: WorkfrontLookup): Promise<LookupListRow[]> =>
		apiClient.get(`${BASE}/lookups/${lookup}`).then(data<LookupListRow[]>),
	values: ({ axis, match, waiting }: ValueListParams = {}): Promise<ValueListRow[]> =>
		apiClient
			.get(`${BASE}/values`, {
				params: { axis: axis || undefined, match: match || undefined, waiting: waiting ? 'true' : undefined },
			})
			.then(data<ValueListRow[]>),
	proposals: (axis: string): Promise<IWorkfrontValueProposal[]> =>
		apiClient.get(`${BASE}/values/proposals`, { params: { axis } }).then(data<IWorkfrontValueProposal[]>),
	versions: (): Promise<MappingVersionDTO[]> => apiClient.get(`${BASE}/versions`).then(data<MappingVersionDTO[]>),
	unmappedFields: (): Promise<UnmappedFieldDTO[]> =>
		apiClient.get(`${BASE}/unmapped-fields`).then(data<UnmappedFieldDTO[]>),

	saveDraftRow: (
		kind: WorkfrontMappingKind,
		key: string,
		revision: number,
		row: MappingRow,
	): Promise<MappingDraftDTO> => apiClient.put(keyPath(kind, key), { revision, row }).then(data<MappingDraftDTO>),
	removeDraftRow: (
		kind: WorkfrontMappingKind,
		key: string,
		revision: number,
		revert = false,
	): Promise<MappingDraftDTO> =>
		apiClient
			.delete(keyPath(kind, key), { params: { revision, revert: revert ? 'true' : undefined } })
			.then(data<MappingDraftDTO>),
	discardDraft: (revision: number): Promise<void> =>
		apiClient.delete(`${BASE}/draft`, { params: { revision } }).then(() => undefined),
	preview: (): Promise<MappingPreviewDTO> => apiClient.post(`${BASE}/draft/preview`).then(data<MappingPreviewDTO>),
	publish: (revision: number, summary: string): Promise<MappingVersionDTO> =>
		apiClient.post(`${BASE}/draft/publish`, { revision, summary }).then(data<MappingVersionDTO>),
	quickPublish: (kind: WorkfrontMappingKind, row: MappingRow, summary?: string): Promise<MappingVersionDTO> =>
		apiClient.post(`${BASE}/quick-publish`, { kind, row, summary }).then(data<MappingVersionDTO>),
	republish: (version: number, summary?: string): Promise<MappingVersionDTO> =>
		apiClient.post(`${BASE}/versions/${version}/republish`, { summary }).then(data<MappingVersionDTO>),
	ignoreUnmappedField: (parameterId: string, reason: string): Promise<void> =>
		apiClient
			.post(`${BASE}/unmapped-fields/${encodeURIComponent(parameterId)}/ignore`, { reason })
			.then(() => undefined),

	/** Workfront's custom fields, from the reference copy, for the field picker. */
	parameters: (search?: string): Promise<WorkfrontParameterDTO[]> =>
		apiClient
			.get<PaginatedResponse<WorkfrontParameterDTO>>('/admin/workfront/reference/parameter', {
				params: { 'filters[search]': search || undefined, limit: 100 },
			})
			.then((r) => r.data.data),
	remapBundle: (id: string): Promise<{ bundleId: string }> =>
		apiClient.post<{ bundleId: string }>(`/admin/workfront/bundles/${id}/remap`).then((r) => r.data),
	canonical: (id: string): Promise<BundleCanonicalDTO | null> =>
		apiClient.get(`/admin/workfront/bundles/${id}/canonical`).then(data<BundleCanonicalDTO | null>),
}
